import { useRef, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Spunta, Tabella } from '../componenti/base.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { IconaPiu } from '../componenti/Icone.tsx'
import { useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import { AvvisoModulo } from '../componenti/errori-modulo.tsx'

/**
 * Cataloghi dei servizi bancari e finanziari (D-18).
 *
 * L'amministratore aggiunge voci ai servizi bancari e finanziari della Rev05 e ai motivi di «NESSUNO
 * di questi servizi». Le rotte esistevano (POST /amministrazione/cataloghi/strumento-finanziario e
 * /motivo-nessun-servizio-bancario) ma nessuna pagina le chiamava, e l'amministratore non aveva modo
 * di usarle. Le voci del modulo non si modificano da qui: una voce aggiunta non e' mai esclusiva e si
 * ordina prima di «NESSUNO di questi servizi». Gli elenchi sono quelli dei cataloghi della sessione,
 * che si rileggono dopo ogni aggiunta perche' i moduli della scheda propongano subito la voce.
 */

type VoceStrumento = { codice: string; nome: string; esclusiva?: boolean; aggiunto_da_admin?: boolean }
type VoceMotivo = { codice: string; nome: string; richiede_specifica?: boolean; aggiunto_da_admin?: boolean }
type Catalogo = 'strumento' | 'motivo'

const PERCORSI: Record<Catalogo, string> = {
  strumento: '/amministrazione/cataloghi/strumento-finanziario',
  motivo: '/amministrazione/cataloghi/motivo-nessun-servizio-bancario',
}

export function CataloghiBancari() {
  const { puo, cataloghi, ricaricaCataloghi } = useSessione()
  const gestisce = puo('catalogo.gestisci')
  const [modulo, setModulo] = useState<Catalogo | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)

  const strumenti = (cataloghi?.strumento_finanziario ?? []) as VoceStrumento[]
  const motivi = (cataloghi?.motivo_nessun_servizio_bancario ?? []) as VoceMotivo[]
  const origine = (aggiunta?: boolean) => (aggiunta
    ? <Etichetta tono="informativo">aggiunta dall’amministratore</Etichetta>
    : <Etichetta>voce del modulo</Etichetta>)

  return (
    <>
      <Intestazione titolo="Cataloghi dei servizi bancari"
                    sottotitolo="Voci della sezione Servizi bancari e finanziari della scheda utente (Rev05)." />
      {!gestisce && <Avviso tipo="informazione">I cataloghi si consultano; le voci le aggiunge l’amministratore.</Avviso>}
      {messaggio && <div role="status"><Avviso tipo="successo">{messaggio}</Avviso></div>}
      {!cataloghi && <Attesa />}

      {cataloghi && (
        <>
          <Riquadro titolo="Servizi bancari e finanziari" denso azioni={gestisce
            ? <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => { setMessaggio(null); setModulo('strumento') }}>Aggiungi un servizio</Bottone>
            : undefined}>
            <Tabella intestazioni={['Voce', 'Origine', 'Esclude le altre voci']}
                     vuoto={<Vuoto testo="Il catalogo è vuoto." />}>
              {strumenti.map((v) => (
                <tr key={v.codice}>
                  <td data-etichetta="Voce">{v.nome}</td>
                  <td data-etichetta="Origine">{origine(v.aggiunto_da_admin)}</td>
                  <td data-etichetta="Esclude le altre voci">{v.esclusiva ? 'sì' : 'no'}</td>
                </tr>
              ))}
            </Tabella>
          </Riquadro>

          <Riquadro titolo="Motivi per nessun servizio bancario" denso azioni={gestisce
            ? <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => { setMessaggio(null); setModulo('motivo') }}>Aggiungi un motivo</Bottone>
            : undefined}>
            <Tabella intestazioni={['Voce', 'Origine', 'Richiede un testo']}
                     vuoto={<Vuoto testo="Il catalogo è vuoto." />}>
              {motivi.map((v) => (
                <tr key={v.codice}>
                  <td data-etichetta="Voce">{v.nome}</td>
                  <td data-etichetta="Origine">{origine(v.aggiunto_da_admin)}</td>
                  <td data-etichetta="Richiede un testo">{v.richiede_specifica ? 'sì' : 'no'}</td>
                </tr>
              ))}
            </Tabella>
          </Riquadro>
        </>
      )}

      {modulo && (
        <Modale titolo={modulo === 'strumento' ? 'Aggiungi un servizio bancario o finanziario' : 'Aggiungi un motivo'}
                chiudi={() => setModulo(null)}>
          <ModuloVoce catalogo={modulo} annulla={() => setModulo(null)}
                      aggiunta={async (nome) => {
                        setModulo(null)
                        setMessaggio(`Voce «${nome}» aggiunta.`)
                        await ricaricaCataloghi().catch(() => undefined)
                      }} />
        </Modale>
      )}
    </>
  )
}

function ModuloVoce({ catalogo, annulla, aggiunta }: {
  catalogo: Catalogo; annulla: () => void; aggiunta: (nome: string) => Promise<void>
}) {
  const [nome, setNome] = useState('')
  const [richiedeTesto, setRichiedeTesto] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [erroreNome, setErroreNome] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  async function salva() {
    setErrore(null)
    const pulito = nome.trim()
    if (pulito.length < 2) {
      setErroreNome('Indicare il nome della voce, in almeno due caratteri.')
      setErrore('Correggere i campi segnalati.')
      setTentativo((n) => n + 1)
      return
    }
    setErroreNome(null)
    setInCorso(true)
    try {
      await api.post(PERCORSI[catalogo], catalogo === 'motivo'
        ? { nome: pulito, richiede_specifica: richiedeTesto }
        : { nome: pulito })
      await aggiunta(pulito)
    } catch (e) {
      // Voce gia' presente e nome da cui non si ricava un codice sono errori del campo Nome.
      if (e instanceof ErroreApi && (e.codice === 'VOCE_ESISTENTE' || e.codice === 'CODICE_NON_VALIDO')) {
        setErroreNome(e.codice === 'VOCE_ESISTENTE'
          ? 'Esiste già una voce con questo nome.'
          : 'Dal nome non si ricava un codice: usare lettere e cifre.')
        setErrore('Correggere i campi segnalati.')
      } else {
        setErrore(e instanceof ErroreApi
          ? (e.stato === 404 ? 'Operazione riservata all’amministratore.' : e.message)
          : 'Non è andata a buon fine: controlla la connessione e riprova.')
      }
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <Campo etichetta="Nome" obbligatorio maxLength={300} valore={nome} cambia={setNome} errore={erroreNome}
             aiuto="Come compare nel modulo della scheda utente." />
      {catalogo === 'motivo' && (
        <Spunta etichetta="Richiede un testo" valore={richiedeTesto} cambia={setRichiedeTesto}
                aiuto="Con la voce scelta il modulo chiede di specificare il motivo, come per «Altro motivo»." />
      )}
      <AvvisoModulo errore={errore} errori={erroreNome ? { nome: erroreNome } : {}} etichette={{ nome: 'Nome' }} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>Aggiungi la voce</Bottone>
      </div>
    </form>
  )
}
